import Dropdown from "@/components/shared/Dropdown";
import SearchBar from "@/components/shared/SearchBar";
import { StyledToggleButtonGroup } from "@/components/shared/StyledToggleButtonGroup";
import { ActionButton } from "@/components/shared/buttons/ActionButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, styled, ToggleButton, useTheme } from "@mui/material";
import React from "react";

interface FilterBarProps {
    coverageFilter: string;
    onCoverageFilterChange: (value: string) => void;
    clauseTypeFilter: string;
    onClauseTypeFilterChange: (value: string) => void;
    searchValue: string;
    onSearchChange: (value: string) => void;
    onExpandAll?: () => void;
    onCollapseAll?: () => void;
    totalClauses?: number;
    expandedCount?: number;
    totalPolicyExtractions?: number;
    expandedPolicyExtractionsCount?: number;
    availableClauseTypes?: string[];
    viewMode?: "clauses" | "fields";
    hasFields?: boolean;
    onViewModeChange?: (mode: "clauses" | "fields") => void;
}

const FilterContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    flexWrap: "wrap",
    backgroundColor: theme.palette.grey[50],
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("md")]: {
        rowGap: theme.spacing(1.5),
    },
    [theme.breakpoints.down("sm")]: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
}));

const coverageOptions = [
    { value: "all", label: "All Clauses" },
    { value: "gaps", label: "Gaps Only" },
];

const FilterBar: React.FC<FilterBarProps> = ({
    coverageFilter,
    onCoverageFilterChange,
    clauseTypeFilter,
    onClauseTypeFilterChange,
    searchValue,
    onSearchChange,
    onExpandAll,
    onCollapseAll,
    totalClauses = 0,
    expandedCount = 0,
    totalPolicyExtractions = 0,
    expandedPolicyExtractionsCount = 0,
    availableClauseTypes = [],
    viewMode = "clauses",
    hasFields = false,
    onViewModeChange,
}) => {
    const theme = useTheme();
    // Generate clause type options dynamically
    const clauseTypeOptions = [
        { value: "all", label: "All Clause Types" },
        ...availableClauseTypes.map((type) => ({ value: type, label: type })),
    ];

    // Detect if most clauses are expanded (more than half)
    const expanded =
        viewMode === "clauses"
            ? totalClauses > 0 && expandedCount > totalClauses / 2
            : totalPolicyExtractions > 0 &&
              expandedPolicyExtractionsCount > totalPolicyExtractions / 2;

    const handleExpandAllToggle = React.useCallback(() => {
        if (expanded && onCollapseAll) {
            onCollapseAll();
        } else if (!expanded && onExpandAll) {
            onExpandAll();
        }
    }, [expanded, onExpandAll, onCollapseAll]);

    const handleViewModeChange = (
        _event: React.MouseEvent<HTMLElement>,
        newMode: "clauses" | "fields" | null
    ) => {
        if (newMode !== null && onViewModeChange) {
            onViewModeChange(newMode);
        }
    };

    return (
        <FilterContainer>
            {/* View Mode Toggle */}
            {hasFields && (
                <StyledToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    size="small"
                >
                    <ToggleButton value="clauses">Clauses</ToggleButton>
                    <ToggleButton value="fields">Fields</ToggleButton>
                </StyledToggleButtonGroup>
            )}

            {/* Only show clause-specific filters when in clauses view */}
            {viewMode === "clauses" && (
                <>
                    <Dropdown
                        value={coverageFilter}
                        onChange={onCoverageFilterChange}
                        options={coverageOptions}
                        minWidth={150}
                    />
                    <Dropdown
                        value={clauseTypeFilter}
                        onChange={onClauseTypeFilterChange}
                        options={clauseTypeOptions}
                        minWidth={150}
                    />
                </>
            )}

            <ActionButton
                icon={
                    expanded ? (
                        <KeyboardArrowDownIcon />
                    ) : (
                        <KeyboardArrowUpIcon />
                    )
                }
                onClick={handleExpandAllToggle}
                sx={{
                    backgroundColor: expanded
                        ? theme.palette.background.paper
                        : theme.palette.primary.main,
                    color: expanded
                        ? theme.palette.primary.main
                        : theme.palette.primary.contrastText,
                    border: expanded
                        ? `1px solid ${theme.palette.primary.main}`
                        : "none",
                    "&:hover": {
                        backgroundColor: expanded
                            ? theme.palette.grey[100]
                            : theme.palette.primary.dark,
                    },
                    minWidth: "auto",
                    flexShrink: 0,
                }}
            >
                {expanded ? "Collapse All" : "Expand All"}
            </ActionButton>

            {/* Search bar - available in both views */}
            <SearchBar
                value={searchValue}
                onChange={onSearchChange}
                placeholder={
                    viewMode === "clauses"
                        ? "Search clauses..."
                        : "Search fields..."
                }
            />
        </FilterContainer>
    );
};

export default FilterBar;
